import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '../types';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Function to fetch user data including RP balance
  const fetchUserData = useCallback(async (userId: string): Promise<User | null> => {
    try {
      // First get the user's basic data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (userError) {
        console.error('Error fetching user data:', userError);
        return null;
      }

      if (!userData) return null;

      // Then get the user's RP balance
      const { data: rewardsData, error: rewardsError } = await supabase
        .from('user_rewards')
        .select('rp_balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (rewardsError) {
        console.error('Error fetching rewards data:', rewardsError);
      }

      // Combine user data with RP balance
      return {
        ...userData,
        rp_balance: rewardsData?.rp_balance || 0
      };
    } catch (error) {
      console.error('Error in fetchUserData:', error);
      return null;
    }
  }, []);

  // Function to refresh user data
  const refreshUser = useCallback(async () => {
    if (!initialized) return;

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      if (!session?.user) {
        setUser(null);
        return;
      }

      const userData = await fetchUserData(session.user.id);
      if (userData) {
        setUser(userData);
      } else {
        // Handle case where user data doesn't exist
        toast.error('User data not found');
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) throw signOutError;
        setUser(null);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      toast.error('Failed to refresh user data');
    }
  }, [fetchUserData, initialized]);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        if (session?.user && mounted) {
          const userData = await fetchUserData(session.user.id);
          if (userData && mounted) {
            setUser(userData);
          } else if (mounted) {
            // Handle case where user data doesn't exist
            toast.error('User data not found');
            const { error: signOutError } = await supabase.auth.signOut();
            if (signOutError) throw signOutError;
            setUser(null);
          }
        } else if (mounted) {
          setUser(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) setUser(null);
      } finally {
        if (mounted) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted || !initialized) return;

      try {
        switch (event) {
          case 'SIGNED_IN':
            if (session?.user) {
              const userData = await fetchUserData(session.user.id);
              if (userData && mounted) {
                setUser(userData);
              } else {
                // Handle case where user data doesn't exist
                toast.error('User data not found');
                const { error: signOutError } = await supabase.auth.signOut();
                if (signOutError) throw signOutError;
                if (mounted) setUser(null);
              }
            }
            break;

          case 'SIGNED_OUT':
            if (mounted) setUser(null);
            break;

          case 'TOKEN_REFRESHED':
            if (session?.user) {
              const userData = await fetchUserData(session.user.id);
              if (userData && mounted) {
                setUser(userData);
              } else if (mounted) {
                setUser(null);
              }
            }
            break;
        }
      } catch (error) {
        console.error('Error handling auth state change:', error);
        if (mounted) setUser(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserData, initialized]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      
      // Clear any stored auth data
      localStorage.removeItem('gig-auth-token');
      localStorage.removeItem('supabase.auth.token');
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      
      // Force reload to clear any cached state
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};